import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useSmsVerifyStartMutation } from '@/api/hooks/mutations/use-sms-verify-start-mutation';
import { DataPrivacyStatement } from '@/components/DataPrivacyStatement';
import { GeneralErrorIndicator } from '@/components/GeneralErrorIndicator';
import { NavigationBar } from '@/components/NavigationBar';
import { PhoneNumberInput } from '@/components/PhoneNumberInput';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { useSessionSave } from '@/hooks/use-session-save';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { logger } from '@/lib/logger';
import { phoneNumberSchema } from '@/lib/validation-schemas';

const formId = 'start-phone-verification-form';
const formSchema = z.object({
  phoneNumber: phoneNumberSchema,
});

type StartPhoneVerificationFormData = z.infer<typeof formSchema>;

export function StartPhoneVerification() {
  const navigate = useNavigate();
  const { saveSession } = useSessionSave();
  const smsVerifyStartMutation = useSmsVerifyStartMutation();

  const { contactPhoneNumber } = useSessionStore.getState();

  const form = useForm<StartPhoneVerificationFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      phoneNumber: contactPhoneNumber,
    },
  });

  const handleBack = () => {
    navigate({ to: '/appointment/book-call' });
  };

  const onSubmit = async (formValues: StartPhoneVerificationFormData) => {
    if (!smsVerifyStartMutation.canResend) {
      return;
    }

    try {
      const response = await smsVerifyStartMutation.mutateAsync({
        phoneNumber: `+1${formValues.phoneNumber}`,
      });

      if (!response.success) {
        throw new Error(response.message);
      }

      useSessionStore.getState().updateState({
        contactPhoneNumber: formValues.phoneNumber,
        contactPhoneNumberVerified: false,
      });

      saveSession();

      navigate({ to: '/verify-profile/phone-number/code' });
    } catch (error) {
      logger.error('Failed to send verification code', error);

      form.setError('phoneNumber', { message: 'Failed to send verification code' });
    }
  };

  return (
    <section>
      <NavigationBar onBack={handleBack} backLabel="Book call" />
      <Card>
        <CardHeader>
          <CardTitle>Verify your phone number to confirm your appointment</CardTitle>
        </CardHeader>
        <CardContent className="space-y-8 sm:space-y-12">
          <p className="text-base text-charcoal-500">
            We'll send a code via SMS to verify your phone number
          </p>

          <form
            className="space-y-8 sm:space-y-12"
            onSubmit={form.handleSubmit(onSubmit)}
            id={formId}
          >
            <Controller
              name="phoneNumber"
              control={form.control}
              render={({ field, fieldState }) => {
                const inputId = `${formId}-phoneNumber`;

                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={inputId}>Mobile phone</FieldLabel>
                    <PhoneNumberInput {...field} id={inputId} aria-invalid={fieldState.invalid} />
                    {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                  </Field>
                );
              }}
            />

            {smsVerifyStartMutation.isError && (
              <GeneralErrorIndicator>
                Failed to send verification code. Please try again.
              </GeneralErrorIndicator>
            )}

            <Button
              type="submit"
              disabled={smsVerifyStartMutation.isPending || !smsVerifyStartMutation.canResend}
              className="w-full sm:w-auto sm:min-w-[186px]"
            >
              {getSendCodeLabel(smsVerifyStartMutation)}
            </Button>
          </form>
        </CardContent>
      </Card>

      <DataPrivacyStatement />
    </section>
  );
}

function getSendCodeLabel(mutation: {
  isPending: boolean;
  canResend: boolean;
  remainingMs: number;
}) {
  if (mutation.isPending) return 'Sending code...';
  if (mutation.canResend) return 'Send code';
  return `Send code in ${Math.ceil(mutation.remainingMs / 1000)}s`;
}
